import { Classes, Drawer, InputGroup } from '@blueprintjs/core';
import { IUniversalSearchCriteria } from 'es/app/business-logic/services/universal-search/UniversalSearch';
import SiteEntitySearch from 'es/app/components/layout/universal-search/SiteEntitySearch';
import useLimitedLengthString from 'es/core/hooks/useLimitedLengthString';
import { isGuid } from 'es/core/types/Guid';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import FacilitySearch from './FacilitySearch';
import PersonSearch from './PersonSearch';
import classNames from 'classnames';
import { BootstrapStyles } from 'es/app/visuals/styles/BootstrapStyles';

const EMPTY_CRITERIA = { id: null, text: null };

interface IProps {
    visible: boolean;
    onVisibilityChanged: (visible: boolean) => void;
}

const UniversalSearch = (props: IProps) => {
    const { visible, onVisibilityChanged } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const [searchText, setSearchText] = useLimitedLengthString(40, '');
    const [searchCriteria, setSearchCriteria] = useState<IUniversalSearchCriteria>(EMPTY_CRITERIA);

    const handleChangeSearchText = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };
    const handleOpened = () => inputRef.current?.focus();
    const handleClose = () => {
        setSearchText('');
        onVisibilityChanged(false);
    };

    useSearchCriteria();
    useResetOnLocationChange();

    return (
        <Drawer
            isOpen={visible}
            portalClassName="universal-search"
            onOpened={handleOpened}
            onClose={handleClose}
            title=""
        >
            <div className={Classes.DRAWER_BODY}>
                <InputGroup
                    inputRef={ref => {
                        inputRef.current = ref;
                    }}
                    value={searchText}
                    placeholder="Universal Search"
                    onKeyDown={handleKeyPress}
                    onChange={handleChangeSearchText}
                />

                <div className={classNames('universal-search-results', BootstrapStyles['mt-2'])}>
                    <SiteEntitySearch criteria={searchCriteria} />
                    <PersonSearch criteria={searchCriteria} />
                    <FacilitySearch criteria={searchCriteria} />
                </div>
            </div>
        </Drawer>
    );

    function handleKeyPress(event: React.KeyboardEvent) {
        if (event.key === 'Escape' || event.key === 'Enter') {
            inputRef.current?.blur();
        }
    }

    function useSearchCriteria() {
        const [searchTextDebounced] = useDebounce(searchText, 250);

        useEffect(() => {
            if (!searchTextDebounced) {
                setSearchCriteria(EMPTY_CRITERIA);
            } else if (isGuid(searchTextDebounced)) {
                setSearchCriteria({ id: searchText, text: null });
            } else {
                setSearchCriteria({ id: null, text: searchTextDebounced });
            }
        }, [searchTextDebounced]);
    }

    function useResetOnLocationChange() {
        const location = useLocation();
        useEffect(() => {
            onVisibilityChanged(false);

            setTimeout(() => handleClose());
        }, [location.pathname]);
    }
};

export default UniversalSearch;
